import angular from 'angular'

import generateGradient from './gallery-header.generate-gradient.js'

import { toRgb, toPosPixel } from '../utils/bits'
import { MOBILE_THRESHOLD } from '../utils/shared-constants'

angular
  .module('ggs.gallery-header')
  .factory('GalleryHeaderStyleHandlers', GalleryHeaderStyleHandlersProvider)

function GalleryHeaderStyleHandlersProvider(
  rootQs,
  cssElementQueries,
  GSNApplyStyleTmpl) {

  const OWNER_TAG = __filename

  return function GalleryHeaderStyleHandlers(descBgType) {

    // applyLogoColor

    const LOGO_COLOR_PLH = ':background-image:'
    const LOGO_COLOR_OPACITY_PLH = ':background-opacity:'

    const LOGO_COLOR_TMPL = `
      ${rootQs} .ggs-gallery-header__logo-with-stroke {
        background-color: rgba(${LOGO_COLOR_PLH}, ${LOGO_COLOR_OPACITY_PLH});
      }`

    const applyLogoColor = GSNApplyStyleTmpl({
      propKeys: [
        'logo_background',
        'logo_bg_opacity'
      ],
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      styleFunc: (logo_background, logo_bg_opacity) =>
        LOGO_COLOR_TMPL
          .replace(LOGO_COLOR_PLH, toRgb(logo_background))
          .replace(LOGO_COLOR_OPACITY_PLH, logo_bg_opacity)
    })

    // applyLogoImage

    const LOGO_IMAGE_PLH = ':background-image:'
    const LOGO_IMAGE_TMPL = `
      ${rootQs} .ggs-gallery-header__logo-with-stroke:after {
        background-image: url("${LOGO_IMAGE_PLH}");
      }`

    const applyLogoImage = GSNApplyStyleTmpl({
      propKeys: 'logo',
      ownerTag: OWNER_TAG,
      styleTmpl: LOGO_IMAGE_TMPL,
      stylePlh: LOGO_IMAGE_PLH
    })

    // applyLogoOpacity

    const LOGO_OPACITY_PLH = ':opacity:'
    const LOGO_OPACITY_TMPL = `
      ${rootQs} .ggs-gallery-header__logo-with-stroke:after {
        opacity: ${LOGO_OPACITY_PLH};
      }`

    const applyLogoOpacity = GSNApplyStyleTmpl({
      propKeys: 'logo_opacity',
      ownerTag: OWNER_TAG,
      styleTmpl: LOGO_OPACITY_TMPL,
      stylePlh: LOGO_OPACITY_PLH
    })

    // applyCoverBackground

    const applyCoverBackground = GSNApplyStyleTmpl({
      propKeys: [
        'header_cover_bg_type',
        'image_background',
        'image_bg_opacity',
        'header_background',
        'header_background_2',
        'header_bg_1_opacity',
        'header_bg_2_opacity',
        'header_solid_background',
        'header_solid_bg_opacity',
        'header_gradient_direction'
      ],
      ownerTag: OWNER_TAG,
      styleFunc: buildCoverBackground
    })

    const COVER_BG_PLH = ':cover-background:'
    const COVER_BG_TMPL = `
      ${rootQs} .ggs-gallery-header__cover-background {
        background: ${COVER_BG_PLH};
      }`

    function buildCoverBackground(
      header_cover_bg_type,
      image_background,
      image_bg_opacity,
      header_background,
      header_background_2,
      header_bg_1_opacity,
      header_bg_2_opacity,
      header_solid_background,
      header_solid_bg_opacity,
      header_gradient_direction) {

      const type = descBgType(header_cover_bg_type)
      const solid = { color: header_solid_background, opacity: header_solid_bg_opacity }
      const image = { color: image_background, opacity: image_bg_opacity }
      const grdnt = {
        direction: header_gradient_direction,
        color_1: header_background, opacity_1: header_bg_1_opacity,
        color_2: header_background_2, opacity_2: header_bg_2_opacity
      }
      const style = generateGradient(type, solid, grdnt, image)
      return COVER_BG_TMPL.replace(COVER_BG_PLH, style)
    }

    // applyCoverImageOpacity

    const COVER_IMAGE_OPACITY_PLH = ':image-opacity:'
    const COVER_IMAGE_OPACITY_TMPL = `
      ${rootQs} .ggs-gallery-header__cover-full-image,
      ${rootQs} .ggs-gallery-header__cover-clipped-image {
        opacity: ${COVER_IMAGE_OPACITY_PLH};
      }`

    const applyCoverImageOpacity = GSNApplyStyleTmpl({
      propKeys: 'image_opacity',
      ownerTag: OWNER_TAG,
      styleTmpl: COVER_IMAGE_OPACITY_TMPL,
      stylePlh: COVER_IMAGE_OPACITY_PLH
    })

    // applyTextMaxWidth

    const TEXT_MAX_WIDTH_PLH = ':max-width:'
    const TEXT_MAX_WIDTH_TMPL = `
      ${rootQs} .ggs-gallery-header__text-wrapper {
        min-width: 300px;
        max-width: ${TEXT_MAX_WIDTH_PLH};
      }

      ${rootQs}[max-width~="${MOBILE_THRESHOLD}"] .ggs-gallery-header__text-wrapper {
        max-width: 100%;
      }`

    const applyTextMaxWidth = GSNApplyStyleTmpl({
      propKeys: 'header_content_max_width',
      castKeys: toPosPixel,
      ownerTag: OWNER_TAG,
      styleTmpl: TEXT_MAX_WIDTH_TMPL,
      stylePlh: TEXT_MAX_WIDTH_PLH,
      afterFunc: cssElementQueries.update
    })

    // applyContentBackground

    const applyContentBackground = GSNApplyStyleTmpl({
      propKeys: [
        'header_content_bg_type',
        'header_content_background',
        'header_content_background_2',
        'header_content_bg_opacity',
        'header_content_bg_opacity_2',
        'header_content_solid_background',
        'header_content_solid_bg_opacity',
        'header_content_gradient_direction'
      ],
      ownerTag: OWNER_TAG,
      styleFunc: buildContentBackground
    })

    const CONTENT_BG_PLH = ':content-background:'
    const CONTENT_BG_TMPL = `
      ${rootQs} .ggs-gallery-header__content-wrapper {
        background: ${CONTENT_BG_PLH};
      }`

    function buildContentBackground(
      header_content_bg_type,
      header_content_background,
      header_content_background_2,
      header_content_bg_opacity,
      header_content_bg_opacity_2,
      header_content_solid_background,
      header_content_solid_bg_opacity,
      header_content_gradient_direction) {

      const type = descBgType(header_content_bg_type)
      const solid = { color: header_content_solid_background, opacity: header_content_solid_bg_opacity }
      const grdnt = {
        direction: header_content_gradient_direction,
        color_1: header_content_background, opacity_1: header_content_bg_opacity,
        color_2: header_content_background_2, opacity_2: header_content_bg_opacity_2
      }
      const style = generateGradient(type, solid, grdnt)
      return CONTENT_BG_TMPL.replace(CONTENT_BG_PLH, style)
    }

    // applyNameColor

    const NAME_COLOR_PLH = ':color:'
    const NAME_COLOR_TMPL = `
      ${rootQs} .ggs-gallery-header__name {
        color: ${NAME_COLOR_PLH};
      }`

    const applyNameColor = GSNApplyStyleTmpl({
      propKeys: 'header_color',
      ownerTag: OWNER_TAG,
      styleTmpl: NAME_COLOR_TMPL,
      stylePlh: NAME_COLOR_PLH
    })

    // applyDescColor

    const DESC_COLOR_PLH = ':color:'
    const DESC_COLOR_TMPL = `
      ${rootQs} .ggs-gallery-header__desc {
        color: ${DESC_COLOR_PLH};
      }`

    const applyDescColor = GSNApplyStyleTmpl({
      propKeys: 'description_color',
      ownerTag: OWNER_TAG,
      styleTmpl: DESC_COLOR_TMPL,
      stylePlh: DESC_COLOR_PLH
    })

    // applyLinksColor

    const LINKS_COLOR_PLH = ':color:'
    const LINKS_COLOR_TMPL = `
      ${rootQs} .ggs-gallery-header__desc a {
        color: ${LINKS_COLOR_PLH};
      }`

    const applyLinksColor = GSNApplyStyleTmpl({
      propKeys: 'description_links_color',
      ownerTag: OWNER_TAG,
      styleTmpl: LINKS_COLOR_TMPL,
      stylePlh: LINKS_COLOR_PLH
    })

    // applyLinksDecoration

    const LINKS_DECORATION_PLH = ':text-decoration:'
    const LINKS_DECORATION_TMPL = `
      ${rootQs} .ggs-gallery-header__desc a {
        text-decoration: ${LINKS_DECORATION_PLH};
      }`

    const applyLinksDecoration = GSNApplyStyleTmpl({
      propKeys: 'description_links_decoration',
      ownerTag: OWNER_TAG,
      styleTmpl: LINKS_DECORATION_TMPL,
      stylePlh: LINKS_DECORATION_PLH
    })

    // applyIconsColor

    const ICONS_COLOR_PLH = ':color:'
    const ICONS_COLOR_TMPL = `
      ${rootQs} .ggs-gallery-header__social-icon {
        color: ${ICONS_COLOR_PLH};
      }`

    const applyIconsColor = GSNApplyStyleTmpl({
      propKeys: 'header_icons_color',
      ownerTag: OWNER_TAG,
      styleTmpl: ICONS_COLOR_TMPL,
      stylePlh: ICONS_COLOR_PLH
    })

    return [
      applyLogoColor,
      applyLogoImage,
      applyLogoOpacity,
      applyCoverBackground,
      applyCoverImageOpacity,
      applyTextMaxWidth,
      applyContentBackground,
      applyNameColor,
      applyDescColor,
      applyLinksColor,
      applyLinksDecoration,
      applyIconsColor
    ]
  }
}
